/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext, process */
import React from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Field, reduxForm, formValueSelector } from 'redux-form';

import MarketingCampaignsFieldset from '../campaigns/MarketingCampaignsFieldset';
import { setUserFieldCount } from 'actions/uiActions';
import { isPending } from 'consts/requestStates';
import { getAllCollection, USER_ROLES } from 'actions/collections';
import FormError from 'components/form/FormError';
import SubmitButton from 'components/form/SubmitButton';
import UsersFields from 'components/tenants/UsersFields';


const OWN_SUBDOMAIN = process.env.DJANGO_SITE_SUBDOMAIN;


export class EditCreateOfficeForm extends React.PureComponent {
  componentWillMount() {
    this.props.loadUserRoles();
  }

  render() {
    const {
      requestState,
      handleSubmit,
      onCancel,
      invalid,
      hasCustomTenantDomainValue,
      hasName,
      editing,
      disabled,
      initialValues,
      roleOptions,
      loggedOnUser,
      userCanUpdateRole,
      setUserFieldCount: setUserFieldCountBound,
      userCount,
    } = this.props;

    const pending = isPending(requestState);

    return (
      <div>
        <form onSubmit={handleSubmit}>
          <span className="list-search-container pull-right">
            <label htmlFor="name">{gettext('Name')}</label>
            <FormError name="name" requestState={requestState} />
            <Field
              component="input"
              type="text"
              name="name"
              placeholder={gettext('Office name')}
              props={{ disabled }}
            />

            <div>
              <FormError name="has_custom_tenant_domain" requestState={requestState} />
              <fieldset>
                <label htmlFor="has_custom_tenant_domain">
                  <Field
                    name="has_custom_tenant_domain"
                    component="input"
                    type="radio"
                    value="false"
                    props={{ disabled: editing || disabled }}
                  />
                  {gettext('Use global AAP domain for office ')}
                </label>
                <label htmlFor="has_custom_tenant_domain">
                  <Field
                    name="has_custom_tenant_domain"
                    component="input"
                    type="radio"
                    value="true"
                    props={{ disabled: editing || disabled }}
                  />
                  {gettext('Custom domain for office')}
                </label>
              </fieldset>
            </div>

            <div>
              <label htmlFor="custom_tenant_domain">{gettext('Domain')}</label>
              <FormError name="custom_tenant_domain" requestState={requestState} />
              {hasCustomTenantDomainValue === 'true' ? (
                <Field
                  component="input"
                  type="text"
                  name="custom_tenant_domain"
                  placeholder={gettext('Office Domain')}
                  props={{ disabled: editing || disabled }}
                />
              ) : (
                <input type="text" disabled value={OWN_SUBDOMAIN} />
              )}
            </div>

            {/* Set the Marketing Campaigns at the Office Level */}
            <MarketingCampaignsFieldset requestState={requestState} disabled={disabled} />

            <UsersFields
              editing={editing}
              requestState={requestState}
              initialValues={initialValues && initialValues.users}
              roleOptions={roleOptions && roleOptions.filter(role => role.get('office_scope'))}
              showPassword
              showActive
              disabled={disabled}
              editableFields={['first_name', 'last_name']}
              loggedOnUser={loggedOnUser}
              userCanUpdateRole={userCanUpdateRole}
              setUserFieldCount={setUserFieldCountBound}
              userCount={userCount}
            />

            <FormError name="non_field_errors" requestState={requestState} />

            <button className="button secondary" onClick={onCancel} disabled={pending}>
              {gettext('Cancel')}
            </button>

            <SubmitButton
              disabled={!hasName || disabled}
              valid={!invalid}
              onClick={handleSubmit}
              pending={pending}
              pendingText={editing ? gettext('Updating...') : gettext('Creating...')}
            >
              {editing ? gettext('Update') : gettext('Create')}
            </SubmitButton>
          </span>
        </form>
      </div>
    );
  }
}

EditCreateOfficeForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  requestState: ImmutablePropTypes.map.isRequired,
  invalid: PropTypes.bool.isRequired,
  hasCustomTenantDomainValue: PropTypes.string,
  hasName: PropTypes.bool.isRequired,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  initialValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  loadUserRoles: PropTypes.func.isRequired,
  roleOptions: ImmutablePropTypes.list,
  loggedOnUser: ImmutablePropTypes.map.isRequired,
  userCanUpdateRole: PropTypes.bool,
  setUserFieldCount: PropTypes.func.isRequired,
  userCount: PropTypes.number.isRequired,
};
EditCreateOfficeForm.defaultProps = {
  hasCustomTenantDomainValue: '',
  editing: false,
  disabled: false,
  initialValues: null,
  roleOptions: null,
  userCanUpdateRole: true,
};

const formName = 'createOffice';

const CreateOfficeReduxForm = reduxForm({
  form: formName,
})(EditCreateOfficeForm);

const selector = formValueSelector(formName);

export function mapStateToProps(state) {
  return {
    loggedOnUser: state.loggedOnUser,
    hasCustomTenantDomainValue: selector(state, 'has_custom_tenant_domain'),
    hasName: Boolean(selector(state, 'name')),
    roleOptions: state.collections.getIn([USER_ROLES, 'items']),
    userCount: state.ui.getIn(['userFields', 'userCount'], 0),
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    loadUserRoles: () => dispatch(getAllCollection(USER_ROLES)),
    setUserFieldCount: count => dispatch(setUserFieldCount(count)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateOfficeReduxForm);
